// Builds the printable proposal document (opened in a new window, saved via the browser's "Save as PDF").
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const lines = (s) => String(s || "").split("\n").map((l) => l.trim()).filter(Boolean);
const money = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const T = {
  ar: {
    dir: "rtl", title: "عرض سعر", no: "رقم العرض", issued: "تاريخ الإصدار", valid: "صالح حتى",
    from: "مقدّم العرض", to: "مقدّم إليه", project: "المشروع", projectH: "تفاصيل المشروع", service: "الخدمة", scope: "نطاق العمل",
    deliverables: "التسليمات", exclusions: "المستثنيات", timeline: "المدة والتعديلات", duration: "مدة التنفيذ",
    days: "يوم عمل", starts: "بداية احتساب المدة", revisions: "جولات التعديل", revScope: "تشمل",
    pricing: "السعر والدفع", base: "قيمة العمل", rush: "رسوم التسليم العاجل", total: "الإجمالي", payTerms: "شروط الدفع",
    basis: "أساس التقدير", terms: "الشروط", sign: "الموافقة والتوقيع", name: "الاسم", signature: "التوقيع", date: "التاريخ",
    freelancer: "مقدّم العرض", client: "العميل", print: "حفظ PDF / طباعة", close: "إغلاق",
    about: "أُعدّ هذا العرض باستخدام حاسبة برولانسر لتسعير العمل الحر",
    disclaimer: "إخلاء مسؤولية: برولانسر أداة مساعدة لإعداد عروض الأسعار وليست طرفًا في هذا العرض أو في أي اتفاق ينتج عنه، ولا تتحمل مسؤولية تنفيذه أو الالتزامات الواردة فيه. هذا المستند عرض سعر بين الطرفين، وليس عقدًا موثقًا ولا توقيعًا إلكترونيًا معتمدًا.",
  },
  en: {
    dir: "ltr", title: "Price Proposal", no: "Proposal no.", issued: "Issue date", valid: "Valid until",
    from: "From", to: "To", project: "Project", projectH: "Project details", service: "Service", scope: "Scope of work",
    deliverables: "Deliverables", exclusions: "Exclusions", timeline: "Timeline & revisions", duration: "Duration",
    days: "working days", starts: "Duration starts", revisions: "Revision rounds", revScope: "Covers",
    pricing: "Price & payment", base: "Work value", rush: "Rush delivery fee", total: "Total", payTerms: "Payment terms",
    basis: "Estimate basis", terms: "Terms", sign: "Acceptance & signature", name: "Name", signature: "Signature", date: "Date",
    freelancer: "Freelancer", client: "Client", print: "Save PDF / Print", close: "Close",
    about: "Prepared with the Prolancer freelance pricing calculator",
    disclaimer: "Disclaimer: Prolancer is a tool that helps prepare price proposals. It is not a party to this proposal or any resulting agreement and bears no responsibility for its execution or obligations. This document is a price proposal between the two parties — not a notarized contract or a certified e-signature.",
  },
};

const list = (arr) => `<ul>${arr.map((l) => `<li>${esc(l)}</li>`).join("")}</ul>`;
const daysWord = (lang, n, fallback) => {
  if (lang !== "ar") return n === 1 ? "working day" : "working days";
  if (n === 2) return "يوما عمل";
  if (n >= 3 && n <= 10) return "أيام عمل";
  return fallback;
};

export function buildProposalHTML({ lang, data: d }) {
  const L = T[lang] || T.ar;
  const font = lang === "ar" ? '"Tajawal"' : '"Outfit"';
  const base = Math.max(0, d.total - (d.rush ? d.rushFee : 0));
  const deliverables = lines(d.deliverables);
  const exclusions = lines(d.exclusions);
  const terms = lines(d.terms);
  const sigBox = (who, nm) => `<div class="sig"><div class="sig-h">${esc(who)}</div>
    <div class="sig-row"><span>${esc(L.name)}:</span><b>${esc(nm || "")}</b></div>
    <div class="sig-row"><span>${esc(L.signature)}:</span><i></i></div>
    <div class="sig-row"><span>${esc(L.date)}:</span><i></i></div></div>`;

  return `<!doctype html><html lang="${lang}" dir="${L.dir}"><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${esc(L.title)} ${esc(d.quoteNo)} — ${esc(d.projectTitle)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" /><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&family=Outfit:wght@400;600;700&display=swap" rel="stylesheet" />
<style>
  * { box-sizing: border-box; }
  @page { size: A4; margin: 0; }
  html, body { margin: 0; }
  body { font-family: ${font}, system-ui, sans-serif; color: #0f172a; background: #fff; font-size: 12.5px; line-height: 1.7; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { max-width: 210mm; margin: 0 auto; padding: 14mm 15mm 12mm; }
  .num { font-variant-numeric: tabular-nums; direction: ltr; unicode-bidi: isolate; }
  header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 12px; border-bottom: 3px solid #18c98f; }
  header h1 { margin: 0; font-size: 26px; font-weight: 700; letter-spacing: 0; }
  header .no { margin-top: 2px; font-size: 12px; color: #475569; }
  .meta { text-align: end; font-size: 12px; color: #334155; }
  .meta b { color: #0f172a; }
  section { margin-top: 14px; break-inside: avoid; page-break-inside: avoid; }
  h2 { margin: 0 0 6px; font-size: 13.5px; font-weight: 700; color: #182138; padding-bottom: 3px; border-bottom: 1px solid #e2e8f0; break-after: avoid; page-break-after: avoid; }
  .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .party { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; }
  .party .lbl { font-size: 11px; color: #64748b; font-weight: 600; }
  .party .nm { font-size: 14px; font-weight: 700; }
  .kv { display: grid; grid-template-columns: 140px 1fr; gap: 4px 12px; }
  .kv span { color: #64748b; }
  p { margin: 0; white-space: pre-line; }
  ul { margin: 0; padding-inline-start: 18px; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
  td.amt { text-align: end; font-weight: 600; white-space: nowrap; }
  tr.total td { font-size: 15px; font-weight: 700; background: #ecfdf5; border-bottom: 2px solid #18c98f; }
  .sigs { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .sig { border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px 12px; }
  .sig-h { font-weight: 700; margin-bottom: 6px; }
  .sig-row { display: flex; gap: 8px; align-items: flex-end; margin-top: 10px; }
  .sig-row span { color: #64748b; min-width: 52px; }
  .sig-row i { flex: 1; border-bottom: 1px dashed #94a3b8; height: 18px; }
  footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #64748b; break-inside: avoid; }
  footer .about { display: flex; align-items: center; gap: 6px; font-weight: 600; color: #334155; }
  footer img { width: 16px; height: 14px; object-fit: contain; }
  .bar { position: fixed; top: 10px; inset-inline-end: 10px; display: flex; gap: 8px; }
  .bar button { border: 0; border-radius: 999px; padding: 10px 16px; font: 700 13px ${font}, sans-serif; cursor: pointer; box-shadow: 0 8px 20px -8px rgba(15,23,42,.4); }
  .bar .p { background: #18c98f; color: #182138; } .bar .c { background: #182138; color: #fff; }
  @media print { .bar { display: none !important; } .page { padding: 14mm 15mm 12mm; } }
  @media (max-width: 640px) { .parties, .sigs { grid-template-columns: 1fr; } .kv { grid-template-columns: 110px 1fr; } }
</style></head><body>
<div class="bar"><button class="c" onclick="window.close()">${esc(L.close)}</button><button class="p" onclick="plPrint()">${esc(L.print)}</button></div>
<div class="page">
  <header>
    <div><h1>${esc(L.title)}</h1><div class="no">${esc(L.no)}: <b class="num">${esc(d.quoteNo)}</b></div></div>
    <div class="meta"><div>${esc(L.issued)}: <b>${esc(d.issueStr)}</b></div><div>${esc(L.valid)}: <b>${esc(d.validStr)}</b></div></div>
  </header>

  <section class="parties">
    <div class="party"><div class="lbl">${esc(L.from)}</div><div class="nm">${esc(d.freelancerName)}</div><div class="num">${esc(d.freelancerContact)}</div></div>
    <div class="party"><div class="lbl">${esc(L.to)}</div><div class="nm">${esc(d.clientName || "—")}</div><div class="num">${esc(d.clientContact)}</div></div>
  </section>

  <section><h2>${esc(L.projectH)}</h2>
    <div class="kv"><span>${esc(L.project)}</span><b>${esc(d.projectTitle)}</b><span>${esc(L.service)}</span><b>${esc(d.serviceName)}</b></div>
    ${d.scope?.trim() ? `<div style="margin-top:6px"><span style="color:#64748b">${esc(L.scope)}:</span><p>${esc(d.scope.trim())}</p></div>` : ""}
  </section>

  ${deliverables.length ? `<section><h2>${esc(L.deliverables)}</h2>${list(deliverables)}</section>` : ""}
  ${exclusions.length ? `<section><h2>${esc(L.exclusions)}</h2>${list(exclusions)}</section>` : ""}

  <section><h2>${esc(L.timeline)}</h2>
    <div class="kv">
      <span>${esc(L.duration)}</span><b><span class="num">${esc(d.deliveryDays)}</span> ${esc(daysWord(lang, Number(d.deliveryDays), L.days))}</b>
      <span>${esc(L.starts)}</span><b>${esc(d.startPointStr)}</b>
      <span>${esc(L.revisions)}</span><b class="num">${esc(d.revisionsCount)}</b>
      <span>${esc(L.revScope)}</span><b>${esc(d.revisionsScope)}</b>
    </div>
  </section>

  <section><h2>${esc(L.pricing)}</h2>
    <table>
      <tr><td>${esc(L.base)}</td><td class="amt"><span class="num">${money(base)}</span> ${esc(d.currencySymbol)}</td></tr>
      ${d.rush && d.rushFee ? `<tr><td>${esc(L.rush)}</td><td class="amt"><span class="num">${money(d.rushFee)}</span> ${esc(d.currencySymbol)}</td></tr>` : ""}
      <tr class="total"><td>${esc(L.total)}</td><td class="amt"><span class="num">${money(d.total)}</span> ${esc(d.currencySymbol)}</td></tr>
    </table>
    <div class="kv" style="margin-top:8px"><span>${esc(L.payTerms)}</span><b>${esc(d.payTermStr)}</b></div>
  </section>

  ${terms.length ? `<section><h2>${esc(L.terms)}</h2>${list(terms)}</section>` : ""}

  <section><h2>${esc(L.sign)}</h2><div class="sigs">${sigBox(L.freelancer, d.freelancerName)}${sigBox(L.client, d.clientName)}</div></section>

  <footer>
    <div class="about"><img src="${window.location.origin}/prolancer-bird.png" alt="" />${esc(L.about)} — <a href="https://prolancer.app" style="color:inherit;text-decoration:none" dir="ltr">prolancer.app</a></div>
    <div style="margin-top:4px">${esc(L.disclaimer)}</div>
  </footer>
</div>
<script>
  function plPrint(){ try { window.opener && window.opener.postMessage({ pl: "pdf_download" }, window.location.origin === "null" ? "*" : window.location.origin); } catch(e){} window.print(); }
  window.addEventListener("load", function(){ var r = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve(); r.then(function(){ var b = document.querySelector(".bar .p"); if (b) b.focus(); }); });
</script>
</body></html>`;
}
